/* eslint-disable camelcase */
import React from 'react'
import PropTypes from 'prop-types'
import Container from '@material-ui/core/Container'
import Grid from '@material-ui/core/Grid'
import Box from '@material-ui/core/Box'
import { NextSeo } from 'next-seo'
import defaults from '@config/defaults'
import ArticleHeader from '@components/story-header'
import ArticleHero from '@components/story-hero'
import TagList from '@components/tag-list'
import RenderRichText from '@components/renderers/render-rich-text'
import Related from '@components/list-related'
import Recommended from '@components/list-recommended'
import CoachCard from '@components/coach-card'
import ContentPromoBlock from '@components/content-promo-block'
import { JsonLd } from '@components/atoms'

import {
  ArticlePageRoot,
  ArchiveContent,
  PageTop,
  StoryGrid,
} from './story.styles'

const ArticlePage = (props) => {
  const {
    archive,
    audience,
    author,
    headline,
    body,
    image,
    video,
    heroVideoEmbed,
    hero_image,
    heroCaption,
    heroSource,
    tagsCollection,
    recommended,
    related,
    person,
    summary,
    slug,
    url,
    youtube_id,
    coachProfile,
    promoBlock,
    published,
    created_date,
  } = props

  const {
    facilities,
    bookable,
    displayName,
    firstName,
    lastName,
    bookingUrl,
    city,
    state,
    customSlug,
    bio,
    title,
  } = coachProfile?.[0] || {}

  const coachInfoShim = {
    profilePhoto: person?.image?.url,
    displayName: displayName || '',
    city: city || '',
    state: state || '',
    customSlug: customSlug || '',
    firstName: firstName || '',
    lastName: lastName || '',
    facilities: facilities || [],
    bookable: bookable || false,
    bookingURL: bookingUrl || '', // note case-difference in prop name
    bio: bio || '',
    title: title || '',
  }

  const seoImage = (image && image.url) || hero_image
  const storyUrl = slug ? `${defaults.baseUrl}story/${slug}` : null
  const archiveUrl = archive ? `${defaults.baseUrl}archive/${url}` : null
  const seoUrl = archive ? archiveUrl : storyUrl

  const archiveYoutube = youtube_id && {
    provider: 'youtube',
    id: youtube_id,
  }
  return (
    <ArticlePageRoot data-gtm-audience={audience || 'Other'}>
      <NextSeo
        noindex={defaults.noindexNofollow}
        nofollow={defaults.noindexNofollow}
        title={headline}
        description={summary}
        canonical={seoUrl}
        openGraph={{
          title: headline,
          type: 'article',
          url: seoUrl,
          images: [{ url: seoImage }],
        }}
        additionalMetaTags={[
          {
            name: 'twitter:card',
            content: 'summary_large_image',
          },
        ]}
      />
      <JsonLd
        {...{
          '@type': 'NewsArticle',
          description: `${summary}`,
          headline: `${headline}`,
          image: `${seoImage}`,
          dateCreated: `${published || created_date}`,
          datePublished: `${published || created_date}`,
          isAccessibleForFree: true,
          author: author
            ? { '@type': 'Person', name: `${author}` }
            : { '@type': 'Organization', name: 'PGA' },
          mainEntityOfPage: `${seoUrl}`,
          publisher: {
            '@type': 'Organization',
            name: 'PGA of America',
            logo: {
              '@type': 'ImageObject',
              url: 'https://www.pga.com/images/logo-blue.png',
            },
            url: 'https://www.pga.com',
            sameAs: [
              'https://www.facebook.com/PGAofAmerica',
              'https://twitter.com/pga',
              'https://www.linkedin.com/company/pga-of-america/',
              'https://www.instagram.com/Pga/',
              'https://www.youtube.com/channel/UCnUYyAtDiyeaobSzKqVE_BA',
              'https://www.tiktok.com/@pgaofamerica',
            ],
          },
        }}
      />
      <Container maxWidth="md" component={PageTop} data-cy="article-header">
        <ArticleHeader {...props} />
      </Container>

      {(image || video || hero_image || heroVideoEmbed || archiveYoutube) && (
        <Container>
          <ArticleHero
            archiveImage={hero_image}
            image={image}
            video={video}
            youtubeId={youtube_id}
            heroCaption={heroCaption}
            heroSource={heroSource}
            heroVideoEmbed={heroVideoEmbed || archiveYoutube}
            headline={headline}
          />
        </Container>
      )}

      <Box maxWidth="1440px" mx="auto" mb={[6, 6, 8]}>
        <Container data-cy="article-body">
          <StoryGrid container spacing={6} justifyContent="center">
            <Grid item xs={12} md={8}>
              <Box pr={[0, 0, 3]}>
                {archive
                  ? (
                    <>
                      <ArchiveContent dangerouslySetInnerHTML={{ __html: body }} />
                      {promoBlock && <ContentPromoBlock {...promoBlock} />}
                    </>
                  )
                  : body && <RenderRichText {...body} />
                }
              </Box>

              {tagsCollection && <TagList tags={tagsCollection.items} />}
              {promoBlock && !archive && <ContentPromoBlock {...promoBlock} />}
            </Grid>

            {(person || related) && (
              <Grid item xs={12} md={4} component={Box} display="flex" flexDirection="column" justifyContent="flex-start" alignItems="center">
                {person && (
                  <CoachCard coachInfo={{ ...coachInfoShim }} source="stories" isStory />
                )}
                {related && <Related {...related} />}
              </Grid>
            )}

          </StoryGrid>
        </Container>
      </Box>

      {recommended && <Recommended {...recommended} />}

    </ArticlePageRoot>
  )
}

ArticlePage.propTypes = {
  archive: PropTypes.bool,
  audience: PropTypes.string,
  author: PropTypes.string,
  slug: PropTypes.string,
  url: PropTypes.string,
  headline: PropTypes.string,
  published: PropTypes.string,
  created_date: PropTypes.string,
  title: PropTypes.string,
  body: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.string,
  ]),
  image: PropTypes.shape(),
  video: PropTypes.shape(),
  recommended: PropTypes.shape(),
  related: PropTypes.shape(),
  series: PropTypes.string,
  hero_image: PropTypes.string,
  heroSource: PropTypes.string,
  heroCaption: PropTypes.string,
  person: PropTypes.object,
  summary: PropTypes.string,
  tagsCollection: PropTypes.shape({
    items: PropTypes.array,
  }),
  heroVideoEmbed: PropTypes.shape({
    id: PropTypes.string,
    provider: PropTypes.string,
  }),
  youtube_id: PropTypes.string,
  coachProfile: PropTypes.object,
  promoBlock: PropTypes.object,
}

export default ArticlePage
